import { MEMBERSHIP_PLANS, type MembershipPlan } from '@/lib/constants/membership-plans';
import type { PaymentHistoryItem } from '@/lib/types/payment';

/**
 * 멤버십 화면의 계산 — 최근 결제 요약, 플랜 추천, 예상 절약. 컴포넌트 밖에 두는 이유는 이 레포의
 * 테스트가 node 환경에서 `.ts` 만 돌리기 때문이다(`vitest.config.ts`).
 *
 * 숫자는 전부 **실제 결제 이력**에서 나온다. 기획 시안의 「월 약 10만원 절약」 같은 예시 값을
 * 화면에 박지 않는다 — 결제가 없으면 절약 줄 자체를 그리지 않는다(`estimateMonthlySaving` 이 0).
 */

export interface RecentPaymentSummary {
  /** 기간 안의 결제 합계(원). 취소분은 뺀다. */
  total: number;
  /** `total / months`. 결제가 없으면 0. */
  monthlyAverage: number;
  /** 기간 안의 결제 건수. */
  count: number;
  months: number;
}

/** 서버의 `paidAt` 은 `YYYY-MM-DD HH:MM:SS` 다(`contractDateFormatter` 가 공백으로 자른다). Safari 는 공백을 못 읽는다. */
export const parsePaidAt = (paidAt: string): Date => new Date(paidAt.replace(' ', 'T'));

/**
 * 서버 금액은 `BigDecimal` 을 문자열로 보낸다(`@JsonFormat(shape = STRING)`, 컬럼은 `decimal(13,4)`) —
 * `"500000.0000"` 처럼 소수점 넷째 자리가 붙어 올 수 있다. 숫자 외 문자를 전부 걷어내면 `5000000000` 이
 * 되어 만 배가 튄다(1라운드 리뷰 HIGH). 쉼표·공백만 걷어내고 소수로 읽은 뒤 원 단위로 반올림한다.
 */
export const toWon = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/**
 * `now` 에서 `months` 개월 전, 같은 날짜. 그 달에 그 날짜가 없으면(5/31 − 3개월 = 2/31) **그 달의 말일**로
 * 고정한다 — `setMonth` 에 맡기면 3/3 으로 넘어가 2월 말 결제가 빠진다(1라운드 리뷰 MEDIUM).
 */
export const recentCutoff = (now: Date, months: number): Date => {
  const year = now.getFullYear();
  const month = now.getMonth() - months;
  const lastDayOfTarget = new Date(year, month + 1, 0).getDate();
  const day = Math.min(now.getDate(), lastDayOfTarget);
  return new Date(year, month, day, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
};

/**
 * 순결제로 세는 상태. 부분취소(`PARTIAL_CANCELLED`)는 남은 결제액이 있으므로 `paymentPrice − cancelledPrice`
 * 로 센다. 전액취소·대기·실패는 돈이 오간 게 아니다.
 */
const NET_PAID_STATUSES = new Set(['COMPLETED', 'PARTIAL_CANCELLED']);

export const isNetPaid = (item: PaymentHistoryItem): boolean => item.paymentStatus !== null && NET_PAID_STATUSES.has(item.paymentStatus);

export const summarizeRecentPayments = (items: readonly PaymentHistoryItem[], now: Date, months: number): RecentPaymentSummary => {
  const cutoff = recentCutoff(now, months);
  const recent = items.filter(item => isNetPaid(item) && item.paidAt !== null && parsePaidAt(item.paidAt) >= cutoff);
  const total = recent.reduce((sum, item) => sum + toWon(item.paymentPrice) - toWon(item.cancelledPrice), 0);
  return {
    total,
    monthlyAverage: recent.length === 0 ? 0 : Math.round(total / months),
    count: recent.length,
    months,
  };
};

/**
 * 월 평균 결제를 크레딧으로 덮는 **가장 가벼운** 플랜. 결제가 없거나 적으면 Bronze 다(기획의 기본 추천).
 * 가장 큰 플랜으로도 못 덮으면 그 플랜이다 — 더 위가 없다.
 */
export const recommendPlan = (monthlyAverage: number): MembershipPlan => MEMBERSHIP_PLANS.find(plan => plan.monthlyCredit >= monthlyAverage) ?? MEMBERSHIP_PLANS[MEMBERSHIP_PLANS.length - 1];

/**
 * 이 플랜이면 한 달에 얼마를 아끼나.
 *
 * 크레딧 안에서 쓰는 만큼은 월 요금만 내면 되므로 `min(평균, 크레딧) − 요금` 이다. 평균이 요금보다
 * 작으면 음수 → 0(절약이 없다). 화면은 0 이면 절약 줄을 그리지 않는다.
 */
export const estimateMonthlySaving = (monthlyAverage: number, plan: MembershipPlan): number => Math.max(0, Math.min(monthlyAverage, plan.monthlyCredit) - plan.monthlyPrice);

/** `+20%` 같은 추가혜택 표기. 크레딧이 요금보다 얼마나 많은가. */
export const bonusRateLabel = (plan: MembershipPlan): string => `+${Math.round(((plan.monthlyCredit - plan.monthlyPrice) / plan.monthlyPrice) * 100)}%`;
